import React, {useEffect, useMemo, useState} from 'react';
import {useScroll} from 'coreHooks';

interface IntersectionEntryLike {
	isIntersecting: boolean;
	target: Element;
}

/**
 * Custom hook that provides priority intersection detection using manual calculations as primary method.
 * IntersectionObserver has low priority and may trigger too late in some applications.
 * This hook bypasses that limitation, but it's more resource-intensive, so use it only when necessary.
 */
const useIntersection = <T extends HTMLElement>(
	ref: React.RefObject<T>,
	options?: { rootMargin?: number }
): IntersectionEntryLike => {
	const [entry, setEntry] = useState<IntersectionEntryLike>({isIntersecting: false, target: null});
	const {onScroll, unBind} = useScroll();

	useEffect(() => {
		const el = ref.current;

		if (!el) {
			return;
		}

		const checkVisibility = (element: HTMLElement): boolean => {
			const style = window.getComputedStyle(element);

			return style.display !== 'none' &&
			       style.visibility !== 'hidden' &&
			       parseFloat(style.height) !== 0 &&
			       parseFloat(style.width) !== 0 &&
			       parseFloat(style.opacity) !== 0;
		};

		const checkInview = () => {
			if (!checkVisibility(el)) {
				setEntry({isIntersecting: false, target: el});

				return;
			}

			const rect = el.getBoundingClientRect();
			const inView =
				rect.top < window.innerHeight + (options?.rootMargin || 0) &&
				rect.bottom > 0 - (options?.rootMargin || 0);

			setEntry({isIntersecting: inView, target: el});
		};

		checkInview();

		const scrollIndex = onScroll(checkInview);

		window.addEventListener('resize', checkInview);

		return () => {
			unBind(scrollIndex);
			window.removeEventListener('resize', checkInview);
		};
	}, []);

	return useMemo(() => entry, [entry.isIntersecting]);
};

export default useIntersection;
